import { useState } from "react";
import { FieldNameSchema, type ProfileField } from "@starsach/shared-types";
import { Card, DataTable, EmptyState, btnPrimary, inputClass } from "../../../components/ui";
import { TabMenu, type CsvShape } from "./TabMenu";

type Kv = Extract<ProfileField, { type: "kv" }>;

const shape: CsvShape<Kv> = {
  name: "kv-pairs",
  headers: ["name", "value"],
  example: [
    { type: "kv", name: "callback_number", value: "+18005550100" },
    { type: "kv", name: "max_queue_wait", value: "300" },
  ],
  toRow: (k) => ({ name: k.name, value: k.value }),
  fromRow: (r, line) => {
    const name = (r["name"] ?? "").trim();
    if (!FieldNameSchema.safeParse(name).success) return `Line ${line}: "${name}" is not a name a flow can read - lower-case letters, digits and underscores.`;
    return { type: "kv", name, value: r["value"] ?? "" };
  },
};

/** Key-value pairs: a name the flow reads and the value it gets - `$.External.kv.<name>`. Edited in place; imported and exported as CSV. */
export function KvTab({ fields, draft, onUpdate, onAdd, onRemove, onReplaceAll }: { fields: Kv[]; draft: ProfileField[]; onUpdate: (field: ProfileField, next: ProfileField) => void; onAdd: (field: ProfileField) => void; onRemove: (field: ProfileField) => void; onReplaceAll: (fields: Kv[]) => void }) {
  const [name, setName] = useState("");
  const [value, setValue] = useState("");
  const [problem, setProblem] = useState<string>();
  return (
    <Card title="Key-value pairs">
      <form
        className="mb-4 flex flex-wrap items-end gap-3 rounded border border-dashed border-neutral-300 p-3 dark:border-neutral-700"
        aria-label="Add a key-value pair"
        onSubmit={(e) => {
          e.preventDefault();
          const n = name.trim();
          if (!FieldNameSchema.safeParse(n).success) return setProblem("A name is lower-case letters, digits and underscores, starting with a letter or digit: callback_number, max_queue_wait.");
          if (draft.some((f) => f.type === "kv" && f.name === n)) return setProblem(`There is already a pair called ${n}.`);
          onAdd({ type: "kv", name: n, value });
          setName("");
          setValue("");
          setProblem(undefined);
        }}
      >
        <label className="text-xs">
          Name
          <input className={`${inputClass} w-56`} value={name} placeholder="callback_number" aria-label="New pair name" onChange={(e) => { setName(e.target.value); setProblem(undefined); }} />
        </label>
        <label className="text-xs flex-1">
          Value
          <input className={inputClass} value={value} placeholder="+18005550100" aria-label="New pair value" onChange={(e) => setValue(e.target.value)} />
        </label>
        <button type="submit" className={btnPrimary}>Add pair</button>
        <span className="ml-auto">
          <TabMenu
            what="Key-value pairs"
            shape={shape}
            items={fields}
            onImport={(items, replace) => {
              const byName = new Map((replace ? [] : fields).map((k) => [k.name, k]));
              for (const k of items) byName.set(k.name, k);
              onReplaceAll([...byName.values()]);
            }}
            onDeleteAll={() => onReplaceAll([])}
          />
        </span>
        {problem && <p role="alert" className="basis-full text-xs text-red-700 dark:text-red-300">{problem}</p>}
      </form>
      <DataTable
        caption="Key-value pairs in this profile"
        rows={fields}
        rowKey={(k) => k.name}
        sortBy={(k) => k.name}
        filter={fields.length > 5}
        empty={<EmptyState title="No pairs yet" hint="A pair is a name the flow reads and the value it gets: $.External.kv.<name>. Add one above, or import a CSV." />}
        columns={[
          { key: "name", header: "Name", render: (k) => <code className="text-xs">{k.name}</code> },
          { key: "value", header: "Value", render: (k) => <input className={`${inputClass} mt-0`} value={k.value} aria-label={`Value of ${k.name}`} onChange={(e) => onUpdate(k, { ...k, value: e.target.value })} /> },
          { key: "remove", header: "", render: (k) => <button type="button" className="text-xs text-red-700 underline dark:text-red-300" onClick={() => onRemove(k)} aria-label={`Remove ${k.name}`}>Remove</button> },
        ]}
      />
      <p className="mt-3 text-xs text-neutral-500">The flow reads a pair by name - <code>$.External.kv.callback_number</code> - so order never matters.</p>
    </Card>
  );
}
